import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiErrorText } from '../lib/apiErrorText'
import { api, type MeResponse } from '../api/client'
import { createBrief, createBriefGroup, findArticles, getSettings, type ArticleHit } from './api'
import { campaignHref, groupHref } from './nav'
import { parseDollars } from './pnl'
import { area, btn, btnPrimary, card, ErrorLine, focus, inp, lbl } from './ui'

/** The Title picker's value for one brief on every title open on the desk. */
const EVERY = '__all'

const PLACEHOLDER = 'Famous P-38 Lightning Pilots is on the Flight Line (article 28273). Send it to members who expired July through September 2026.'

/** One line in, a drafted package out: the operator names the title and the idea. */
export function NewBriefPage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const settings = useQuery({ queryKey: ['campaign-desk', 'settings'], queryFn: getSettings, retry: false, staleTime: 30_000 })
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })
  const isAdmin = me?.role === 'admin'
  const titles = settings.data?.titles ?? []
  const [picked, setPicked] = useState('')
  // Until the operator picks, the first title that is open.
  const title = picked || titles.find((t) => t.enabled)?.slug || ''
  const every = title === EVERY
  const [ignoreLimits, setIgnoreLimits] = useState(false)
  const [brief, setBrief] = useState('')
  const [budget, setBudget] = useState('')
  const [q, setQ] = useState('')
  const [debounced, setDebounced] = useState('')
  const [article, setArticle] = useState<ArticleHit | null>(null)
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])

  const hits = useQuery({
    queryKey: ['campaign-desk', 'articles', title, debounced],
    queryFn: () => findArticles(title, debounced),
    enabled: !!title && !every && debounced.length >= 2 && !article,
    staleTime: 60_000,
  })

  const budgetCents = budget.trim() ? parseDollars(budget) : null
  const budgetBad = budget.trim() !== '' && budgetCents === null
  const ready = !!title && brief.trim().length >= 10 && !budgetBad

  const create = useMutation({
    mutationFn: async (): Promise<string> => {
      const fields = { brief: brief.trim(), budget_cents: budgetCents, ignore_limits: isAdmin && ignoreLimits }
      if (every) return groupHref((await createBriefGroup(fields)).group_id)
      const d = await createBrief({ ...fields, publication_slug: title, article_id: article?.id ?? null })
      qc.setQueryData(['campaign-desk', 'campaign', d.campaign.id], d)
      return campaignHref(d.campaign.id)
    },
    onSuccess: (href) => {
      qc.invalidateQueries({ queryKey: ['campaign-desk', 'list'] })
      navigate(href)
    },
    onError: (e) => setError(apiErrorText(e)),
    onSettled: () => { busy.current = false },
  })

  function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!ready || busy.current) return
    busy.current = true
    setError(null)
    create.mutate()
  }

  return (
    <form onSubmit={submit} className="mx-auto flex max-w-3xl flex-col gap-5">
      <h1 className="font-serif text-[28px] font-medium tracking-tight">New campaign</h1>
      {settings.data && !settings.data.agent_on && (
        <p className="rounded-lg border border-amber-glow/40 bg-amber-wash px-4 py-3 text-[13px] text-paper">
          The agent is off. Your brief is kept and drafted once it is switched back on.
        </p>
      )}
      <div className={`${card} flex flex-col gap-4`}>
        <label className={lbl}>Title
          <select value={title} onChange={(e) => { setPicked(e.target.value); setArticle(null) }} className={inp} required>
            <option value={EVERY} disabled={!titles.some((t) => t.enabled)}>Every active title</option>
            {titles.map((t) => (
              <option key={t.slug} value={t.slug} disabled={!t.enabled}>{t.name}{t.enabled ? '' : ' (available at cutover)'}</option>
            ))}
          </select>
        </label>
        <label className={lbl}>The idea, in a line or two
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={4} maxLength={2000}
                    placeholder={PLACEHOLDER} className={area} required />
        </label>

        {every && (
          <p className="text-[13px] text-paper-dim">
            The agent drafts one campaign for each title open on the desk, in that title's look and to its own members.
            You review and approve each one separately.
          </p>
        )}
        {!every && <div className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-paper/70">Build it around an article (optional)</span>
          {article ? (
            <div className="flex flex-wrap items-center gap-3 rounded-lg border border-ink-400 bg-ink-800 px-3 py-2">
              <span className="flex-1">
                <strong>{article.title}</strong>
                <span className="ml-2 text-[12px] text-paper-faint">#{article.id}</span>
              </span>
              <button type="button" className={btn} onClick={() => { setArticle(null); setQ('') }}>Clear</button>
            </div>
          ) : (
            <>
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title words or article id"
                     className={inp} aria-label="Search articles" />
              {debounced.length >= 2 && hits.data && (
                <ul className="flex flex-col divide-y divide-ink-500 rounded-lg border border-ink-500 bg-ink-800">
                  {hits.data.articles.length === 0 && <li className="px-3 py-2 text-paper-faint">No articles on this title match.</li>}
                  {hits.data.articles.map((a) => (
                    <li key={a.id}>
                      <button type="button" onClick={() => setArticle(a)}
                              className={`flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left hover:bg-ink-600 ${focus}`}>
                        <span>{a.title} <span className="text-[12px] text-paper-faint">#{a.id}</span></span>
                        <span className="shrink-0 text-[12px] text-paper-faint">{a.post_date ? a.post_date.slice(0, 10) : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {hits.error && <ErrorLine text={apiErrorText(hits.error)} />}
            </>
          )}
        </div>}

        <label className={`${lbl} max-w-[260px]`}>Budget ceiling ($, optional{every ? ', per title' : ''})
          <input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="25.00" className={inp} aria-invalid={budgetBad} />
        </label>
        {budgetBad && <ErrorLine text="Type the budget in dollars, like 25 or 25.00." />}
        <label className={`flex items-start gap-2.5 text-[13px] ${isAdmin ? '' : 'opacity-60'}`}>
          <input type="checkbox" className={`mt-0.5 h-4 w-4 ${focus}`} checked={isAdmin && ignoreLimits} disabled={!isAdmin}
                 onChange={(e) => setIgnoreLimits(e.target.checked)} />
          <span>
            <span className="font-semibold text-paper">Ignore the desk's contact limits</span>
            {!isAdmin && <span className="ml-2 text-paper-faint">Admins only</span>}
            <span className="block text-paper-dim">
              Sends even to people emailed in the last {settings.data?.contact_cap_days ?? 7} days or already in another desk campaign.
              {' '}People who unsubscribed, bounced or complained are still left out.
            </span>
          </span>
        </label>
      </div>
      <ErrorLine text={error} />
      <div className="flex gap-3">
        <button type="submit" className={btnPrimary} disabled={!ready || create.isPending}>
          {create.isPending ? 'Sending to the agent…' : every ? 'Draft a campaign for each title' : 'Draft the campaign'}
        </button>
      </div>
      <p className="text-[12px] text-paper-faint">Nothing is sent from here. The agent drafts a package for you to review, and only your approval schedules it.</p>
    </form>
  )
}
